import type { JobExecutionStatus } from '@cdw-ae/common-data';
import { createReducer, on } from '@ngrx/store';
import * as JobsStatusActions from './jobs-status.actions';

export const JOBS_STATUS_FEATURE_KEY = 'jobs-status';

export interface JobsStatusState {
  /** Active workflow executions (status = 'started') - for cache refresh indicators */
  activeExecutions: JobExecutionStatus[];
  /** Most recent finished executions per workflow (for "last updated" display) */
  finishedExecutions: JobExecutionStatus[];
  /** Jobs initiated by the current user (in-progress from Firestore) */
  myJobs: JobExecutionStatus[];
  /** All jobs visible to the current user (in-progress from Firestore) */
  allJobs: JobExecutionStatus[];
  /** Completed jobs for current user (from SQL with Redis caching) */
  myCompletedJobs: JobExecutionStatus[];
  /** All completed jobs for customer (from SQL with Redis caching) */
  allCompletedJobs: JobExecutionStatus[];
  /** Workflow-specific completed executions from SQL (keyed by workflow name) */
  workflowExecutions: Record<string, JobExecutionStatus[]>;
  /** Loading state for my completed jobs */
  myCompletedJobsLoading: boolean;
  /** Loading state for all completed jobs */
  allCompletedJobsLoading: boolean;
  /** Error message for my completed jobs */
  myCompletedJobsError: string | null;
  /** Error message for all completed jobs */
  allCompletedJobsError: string | null;
  /** Currently active tab in the jobs panel */
  activeTab: 'my' | 'all';
  /** Whether Firestore binding is active */
  bound: boolean;
  /** Whether initial data has been loaded */
  loaded: boolean;
  /** Error message if binding failed */
  error: string | null;
  // ------------------------------------------------------------------------
  // SSRM grid selection-lock failsafe state (fix/SSRM-row-activity-ui)
  // ------------------------------------------------------------------------
  /**
   * Pessimistic lock entries hydrated from sessionStorage on app init. Each
   * entry covers the brief render→Firestore-bind window so checkboxes are
   * disabled instantly on same-tab refresh. Cleared per-session when Firestore
   * confirms or release happens. Keyed by gridSessionUuid.
   */
  hydratedLocks: Record<string, HydratedLock>;
  /**
   * Grid sessions whose lock was explicitly released locally (cancel click or
   * quiet reconciliation) but whose Firestore terminal-state delete may not
   * yet have propagated. Selectors treat these as released even if Firestore
   * still shows an active job for them, preventing the lock from "snapping
   * back" between the optimistic clear and the eventual terminal event.
   * Cleared on jobCompleted for the matching jobId.
   */
  releasedLockSessions: string[];
  /**
   * Periodically-ticked clock (ms since epoch). Powers the threshold selector
   * without making it impure. Updated by nowTick$ every 30s.
   */
  nowMs: number;
}

/**
 * Pessimistic lock entry hydrated from sessionStorage. Held until Firestore
 * either confirms an active job for this session (then we use the real
 * Firestore record) or confirms no active job (then we drop this entry).
 */
export interface HydratedLock {
  gridSessionUuid: string;
  jobId: number;
  selectedCount: number;
  lockSetAt: number;
}

export const initialJobsStatusState: JobsStatusState = {
  activeExecutions: [],
  finishedExecutions: [],
  myJobs: [],
  allJobs: [],
  myCompletedJobs: [],
  allCompletedJobs: [],
  workflowExecutions: {},
  myCompletedJobsLoading: false,
  allCompletedJobsLoading: false,
  myCompletedJobsError: null,
  allCompletedJobsError: null,
  activeTab: 'my',
  bound: false,
  loaded: false,
  error: null,
  hydratedLocks: {},
  releasedLockSessions: [],
  nowMs: 0,
};

export const jobsStatusReducer = createReducer(
  initialJobsStatusState,

  on(JobsStatusActions.bindToFirestore, (state) => ({
    ...state,
    bound: true,
    error: null,
  })),

  on(JobsStatusActions.unbindFromFirestore, (state) => ({
    ...state,
    bound: false,
    activeExecutions: [],
    finishedExecutions: [],
    myJobs: [],
    allJobs: [],
    loaded: false,
  })),

  on(JobsStatusActions.activeExecutionsUpdated, (state, { executions }) => ({
    ...state,
    activeExecutions: executions,
    loaded: true,
    error: null,
  })),

  on(JobsStatusActions.finishedExecutionsUpdated, (state, { executions }) => ({
    ...state,
    finishedExecutions: executions,
  })),

  on(JobsStatusActions.bindingError, (state, { error }) => ({
    ...state,
    error,
    loaded: true,
  })),

  on(JobsStatusActions.myJobsUpdated, (state, { executions }) => ({
    ...state,
    myJobs: executions,
    loaded: true,
    error: null,
  })),

  on(JobsStatusActions.allJobsUpdated, (state, { executions }) => ({
    ...state,
    allJobs: executions,
    loaded: true,
    error: null,
  })),

  on(JobsStatusActions.setActiveTab, (state, { tab }) => ({
    ...state,
    activeTab: tab,
  })),

  // Completed jobs from SQL
  on(JobsStatusActions.loadUserExecutions, (state) => ({
    ...state,
    myCompletedJobsLoading: true,
    myCompletedJobsError: null,
  })),

  on(JobsStatusActions.userExecutionsLoaded, (state, { executions }) => ({
    ...state,
    myCompletedJobs: executions,
    myCompletedJobsLoading: false,
    myCompletedJobsError: null,
  })),

  on(JobsStatusActions.userExecutionsError, (state, { error }) => ({
    ...state,
    myCompletedJobsLoading: false,
    myCompletedJobsError: error,
  })),

  on(JobsStatusActions.loadAllExecutions, (state) => ({
    ...state,
    allCompletedJobsLoading: true,
    allCompletedJobsError: null,
  })),

  on(JobsStatusActions.allExecutionsLoaded, (state, { executions }) => ({
    ...state,
    allCompletedJobs: executions,
    allCompletedJobsLoading: false,
    allCompletedJobsError: null,
  })),

  on(JobsStatusActions.allExecutionsError, (state, { error }) => ({
    ...state,
    allCompletedJobsLoading: false,
    allCompletedJobsError: error,
  })),

  // Workflow-specific executions from SQL
  on(
    JobsStatusActions.workflowExecutionsLoaded,
    (state, { workflowName, executions }) => ({
      ...state,
      workflowExecutions: {
        ...state.workflowExecutions,
        [workflowName]: executions,
      },
    })
  ),

  on(JobsStatusActions.workflowExecutionsError, (state) => {
    // Silently ignore workflow execution errors - this data is supplementary
    // If we can't fetch it, we gracefully degrade (no "Last updated" timestamp shown)
    return state;
  }),

  // -------------------------------------------------------------------------
  // SSRM selection-lock failsafe handlers
  // -------------------------------------------------------------------------

  on(
    JobsStatusActions.lockHydratedFromStorage,
    (state, { gridSessionUuid, jobId, selectedCount, lockSetAt }) => ({
      ...state,
      hydratedLocks: {
        ...state.hydratedLocks,
        [gridSessionUuid]: {
          gridSessionUuid,
          jobId,
          selectedCount,
          lockSetAt,
        },
      },
      // Preserve identity of releasedLockSessions when the uuid isn't in it.
      // OnPush components selecting `releasedLockSessions` would otherwise
      // re-render on every hydration even when nothing relevant changed.
      releasedLockSessions: state.releasedLockSessions.includes(gridSessionUuid)
        ? state.releasedLockSessions.filter((s) => s !== gridSessionUuid)
        : state.releasedLockSessions,
    })
  ),

  on(
    JobsStatusActions.bulkJobEnqueued,
    (state, { jobId, gridSessionUuid, selectedCount }) => ({
      ...state,
      hydratedLocks: {
        ...state.hydratedLocks,
        [gridSessionUuid]: {
          gridSessionUuid,
          jobId,
          selectedCount,
          lockSetAt: Date.now(),
        },
      },
      // Identity-preserve when uuid not present (see lockHydratedFromStorage).
      releasedLockSessions: state.releasedLockSessions.includes(gridSessionUuid)
        ? state.releasedLockSessions.filter((s) => s !== gridSessionUuid)
        : state.releasedLockSessions,
    })
  ),

  on(
    JobsStatusActions.selectionLockReleaseRequested,
    (state, { gridSessionUuid }) => ({
      ...state,
      // Optimistic release only — record the trump-card entry so selectors
      // ignore Firestore until terminal-state propagates. `hydratedLocks` is
      // intentionally NOT dropped here: the terminal-state effect
      // (`releaseLockOnJobCompleted$`) needs the `{ jobId }` mapping intact
      // so its fallback path can identify the session when
      // `additionalJobData.gridSessionUuid` is missing. Cleanup of both
      // fields is the job of `selectionLockReleaseConfirmed`.
      releasedLockSessions: state.releasedLockSessions.includes(
        gridSessionUuid
      )
        ? state.releasedLockSessions
        : [...state.releasedLockSessions, gridSessionUuid],
    })
  ),

  on(
    JobsStatusActions.selectionLockReleaseConfirmed,
    (state, { gridSessionUuid }) => {
      // `jobCompleted` fires for every bulk job in every tab. When the
      // confirmed uuid isn't in either field, return state unchanged so
      // OnPush selectors over `hydratedLocks` / `releasedLockSessions`
      // don't churn for every unrelated tab's job.
      const hasLock = gridSessionUuid in state.hydratedLocks;
      const hasReleased = state.releasedLockSessions.includes(gridSessionUuid);
      if (!hasLock && !hasReleased) return state;
      const restLocks = hasLock
        ? (() => {
            const { [gridSessionUuid]: _dropped, ...rest } = state.hydratedLocks;
            return rest;
          })()
        : state.hydratedLocks;
      const restReleased = hasReleased
        ? state.releasedLockSessions.filter((s) => s !== gridSessionUuid)
        : state.releasedLockSessions;
      return {
        ...state,
        hydratedLocks: restLocks,
        releasedLockSessions: restReleased,
      };
    }
  ),

  on(JobsStatusActions.nowTick, (state, { nowMs }) => ({
    ...state,
    nowMs,
  }))
);
